import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUserCircle, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import DelayRender from './DelayRender';
import { db } from '../App';
import { useFireAuth } from '../FireAuthContext';
import './Trades.css';

const Trades = () => {
  const { user } = useFireAuth();
  const navigate = useNavigate();
  const [openTrades, setOpenTrades] = useState([]);
  const [completedTrades, setCompletedTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const tradesPerPage = 4;

  useEffect(() => {
    const fetchTrades = async () => {
      if (user && user.uid) {
        try {
          const tradesRef = collection(db, 'trades');
          const openQuery1 = query(tradesRef, where('from', '==', user.uid), where('status', 'not-in', ['completed', 'cancelled']));
          const openQuery2 = query(tradesRef, where('to', '==', user.uid), where('status', 'not-in', ['completed', 'cancelled']));
          const completedQuery1 = query(tradesRef, where('from', '==', user.uid), where('status', '==', 'completed'));
          const completedQuery2 = query(tradesRef, where('to', '==', user.uid), where('status', '==', 'completed'));
          
          const [openSnapshot1, openSnapshot2, completedSnapshot1, completedSnapshot2] = await Promise.all([
            getDocs(openQuery1), getDocs(openQuery2), getDocs(completedQuery1), getDocs(completedQuery2)
          ]);

          const openTrades = [...openSnapshot1.docs, ...openSnapshot2.docs].map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          const completedTrades = [...completedSnapshot1.docs, ...completedSnapshot2.docs].map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          const fetchDetails = async (trades) => {
            return await Promise.all(trades.map(async (trade) => {
              const otherPlayerId = trade.from === user.uid ? trade.to : trade.from;
              const otherPlayerDocRef = doc(db, 'users', otherPlayerId);
              const otherPlayerDoc = await getDoc(otherPlayerDocRef);
              
              if (!otherPlayerDoc.exists()) {
                console.error(`No user found with ID: ${otherPlayerId}`);
                return null;
              }
              
              const otherPlayerData = otherPlayerDoc.data();
              return {
                ...trade,
                otherPlayer: {
                  name: otherPlayerData.name,
                  profilePicture: otherPlayerData.profilePicture
                }
              };
            }));
          };

          const validOpenTrades = (await fetchDetails(openTrades)).filter(trade => trade !== null);
          const validCompletedTrades = (await fetchDetails(completedTrades)).filter(trade => trade !== null);

          // Sort trades by timestamp in descending order
          const sortedOpenTrades = validOpenTrades.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);
          const sortedCompletedTrades = validCompletedTrades.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);

          setOpenTrades(sortedOpenTrades);
          setCompletedTrades(sortedCompletedTrades);
        } catch (error) {
          console.error("Error fetching trades:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchTrades();
  }, [user]);

  const indexOfLastTrade = currentPage * tradesPerPage;
  const indexOfFirstTrade = indexOfLastTrade - tradesPerPage;
  const currentCompletedTrades = completedTrades.slice(indexOfFirstTrade, indexOfLastTrade);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-container">
      <div className="trades-overview-page">
        <FaArrowLeft className="trades-back-arrow" onClick={() => navigate('/portal/social')} />
        <div className="trades-overview-container">
          <h2 className="trades-title">Open Trades</h2>
          {openTrades.length === 0 ? (
            <p>No open trades at the moment.</p>
          ) : (
            <div className="open-trades-list">
              {openTrades.map((trade) => (
                <div key={trade.id} className="open-trade-item" onClick={() => navigate(`/portal/trade/${trade.id}`)}>
                  <div className="trades-player-info">
                    {trade.otherPlayer.profilePicture ? (
                      <img src={trade.otherPlayer.profilePicture} alt={`${trade.otherPlayer.name}'s profile`} className="trades-player-pic" />
                    ) : (
                      <FaUserCircle className="trades-player-pic-placeholder" />
                    )}
                    <span className="trades-player-name">{trade.otherPlayer.name}</span>
                  </div>
                  <div className="trades-details">
                    <span className="trades-date">{new Date(trade.timestamp.seconds * 1000).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
          <h2 className="completed-trades-title">Completed Trades</h2>
          {completedTrades.length === 0 ? (
            <p>No completed trades at the moment.</p>
          ) : (
            <>
              <div className="completed-trades-grid">
                {currentCompletedTrades.map((trade) => (
                  <div key={trade.id} className="completed-trade-item" onClick={() => navigate(`/portal/trade/${trade.id}`)}>
                    <div className="trades-player-info">
                      {trade.otherPlayer.profilePicture ? (
                        <img src={trade.otherPlayer.profilePicture} alt={`${trade.otherPlayer.name}'s profile`} className="trades-player-pic" />
                      ) : (
                        <FaUserCircle className="trades-player-pic-placeholder" />
                      )}
                      <span className="trades-player-name">{trade.otherPlayer.name}</span>
                    </div>
                    <div className="trades-details">
                      <span className="trades-date">{new Date(trade.timestamp.seconds * 1000).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="pagination">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
                  <FaChevronLeft />
                </button>
                <span>{currentPage}</span>
                <button onClick={() => paginate(currentPage + 1)} disabled={indexOfLastTrade >= completedTrades.length}>
                  <FaChevronRight />
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DelayRender(Trades, 200);
